import React, { useState, useRef, useEffect } from 'react';
import { ParameterDef, ProjectParameters } from '../../types/ProjectParameters';

interface ParamChipProps {
  def: ParameterDef;
  value: string;
  isActive: boolean;
  onChange: (key: keyof ProjectParameters, value: string) => void;
  onRemove: (key: keyof ProjectParameters) => void;
  onToggleActive: (key: keyof ProjectParameters) => void;
}

const ParamChip: React.FC<ParamChipProps> = ({ def, value, isActive, onChange, onRemove, onToggleActive }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const containerClass = isActive
    ? 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100'
    : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100';

  return (
    <div ref={ref} className="relative inline-flex items-center group">
      <div className={`inline-flex items-center gap-1 border rounded-full pl-2.5 pr-1 py-0.5 text-xs transition-colors ${containerClass}`}>
        <button
          onClick={() => onToggleActive(def.key)}
          title={isActive ? 'Désactiver le filtre' : 'Activer le filtre'}
          className={`text-xs ${isActive ? '' : 'line-through'}`}
        >
          {value}
        </button>
        <button
          onClick={() => setIsOpen(o => !o)}
          title="Changer la valeur"
          className="p-0.5 hover:bg-black/5 rounded-full"
        >
          <svg className="w-2.5 h-2.5 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(def.key); }}
        className="ml-0.5 w-3.5 h-3.5 flex items-center justify-center text-gray-400 hover:text-violet-700 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Retirer"
      >
        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px]">
          {def.options.map(opt => (
            <button
              key={opt}
              onClick={() => { onChange(def.key, opt); setIsOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-violet-50 hover:text-violet-700 flex items-center gap-2 ${opt === value ? 'text-violet-700 font-medium' : 'text-gray-700'}`}
            >
              <span className={`w-3 ${opt === value ? 'opacity-100' : 'opacity-0'}`}>✓</span>
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ParamChip;
